import { useEffect, useRef, useState } from 'react';
import { useAgentSessions } from '../../../../api/agent-miner-client/agent-miner-client.hooks';
import { SessionPanel } from '../../session-panel/session-panel';
import { SessionListPanel } from './session-list-panel';
import type { SessionListSideSectionProps } from './session-list-panel.type';

/**
 * Fetching container that pairs {@link SessionListPanel} with the existing
 * session detail panel: the side section shows the scoped session list, and
 * clicking a row swaps to that session's detail in place with a back arrow —
 * the same list ↔ detail exchange the work-unit side section uses.
 *
 * Responsibilities:
 * 1. Fetch the scoped page of sessions from `listParams`
 * 2. Track which session (if any) is drilled into
 * 3. Drop the drill-down whenever the scope changes, so switching to a
 *    different model family can't leave an unrelated session open
 *
 * @param {SessionListSideSectionProps} props - Component props
 * @returns {JSX.Element} The list panel, or the session detail once a row is clicked
 */
export function SessionListSideSection({
  title,
  subtitle,
  listParams,
  onClose,
  onExpand,
  expandLabel,
  emptyLabel,
}: SessionListSideSectionProps) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const { data, query } = useAgentSessions(listParams);

  // The panel stays mounted across scope changes (the parent swaps props, not
  // the component), so an open detail view would otherwise survive a switch to
  // a different model family and show a session that isn't in the new list.
  const scopeKey = JSON.stringify(listParams);
  const previousScopeKey = useRef(scopeKey);
  useEffect(() => {
    if (previousScopeKey.current !== scopeKey) {
      previousScopeKey.current = scopeKey;
      setSelectedSessionId(null);
    }
  }, [scopeKey]);

  if (selectedSessionId) {
    return <SessionPanel sessionId={selectedSessionId} onClose={onClose} onBack={() => setSelectedSessionId(null)} />;
  }

  return (
    <SessionListPanel
      title={title}
      subtitle={subtitle}
      sessions={data?.results ?? []}
      totalCount={data?.count ?? 0}
      isLoading={query.isLoading}
      isError={query.isError}
      onSelectSession={setSelectedSessionId}
      onClose={onClose}
      onExpand={onExpand}
      expandLabel={expandLabel}
      emptyLabel={emptyLabel}
    />
  );
}
